import { useState, useMemo, useEffect } from 'react';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import pluginsService from 'src/services/pluginsService';
import useAuth from 'src/hooks/useAuth';
import { PLUGINS_DESIGN as D } from './Plugins.design';
import getActionClienteByClienteId from 'src/actions/clientes/getActionClienteByClienteId';

const CATEGORIES = [
    { id: 'recursos', label: 'Recursos' },
    { id: 'mensagens', label: 'Mensagens' },
    { id: 'marketing', label: 'Marketing' },
];

const PLAN_BADGE = {
    plus: D.badgePlanPlus,
    essencial: D.badgePlanEssencial,
    mini: D.badgePlanMini,
};

const getPlanBadge = (plan) => {
    const labels = { plus: 'Plus', essencial: 'Essencial', mini: 'Mini' };
    return { label: labels[plan] || plan, className: PLAN_BADGE[plan] || D.badgePlanEssencial };
};

const getCategoryLabel = (cat) => CATEGORIES.find((c) => c.id === cat)?.label || cat;

function PluginCard({ plugin, onClick, onInstall, installing }) {
    const badge = getPlanBadge(plugin.plan);
    const installed = Boolean(plugin.installed);
    const comingSoon = Boolean(plugin.comingSoon);
    const cover = plugin.image || plugin.cover || plugin.thumbnail;

    return (
        <div className={installed ? D.cardInstalled : D.card} onClick={() => onClick(plugin)}>
            <div className={installed ? D.cardCoverInstalled : D.cardCover}>
                {cover ? (
                    <img src={cover} alt="" className={D.cardCoverImg} />
                ) : (
                    <div className={installed ? D.cardCoverFallbackInstalled : D.cardCoverFallback}>
                        <Iconify icon={plugin.icon || 'eva:cube-outline'} width={20} />
                    </div>
                )}
                {installed && !comingSoon && <span className={D.cardStatus}>Instalado</span>}
                {comingSoon && <span className={D.cardStatusSoon}>Em breve</span>}
            </div>

            <div className={D.cardBody}>
                <h3 className={D.cardName}>{plugin.name}</h3>
                <p className={D.cardDesc}>{plugin.description}</p>
                <div className={D.cardMeta}>
                    <span className={`${D.badge} ${badge.className}`}>{badge.label}</span>
                </div>
            </div>

            <div className={installed ? D.cardFooterInstalled : D.cardFooter}>
                <span className={D.cardPrice}>{plugin.price || 'Grátis'}</span>
                <button
                    type="button"
                    className={installed && !comingSoon ? D.cardUninstallBtn : D.cardInstallBtn}
                    disabled={installing || comingSoon}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (comingSoon) return;
                        onInstall(plugin);
                    }}
                >
                    {comingSoon
                        ? 'Em breve'
                        : installing
                            ? (installed ? 'Removendo...' : 'Instalando...')
                            : (installed ? 'Desinstalar' : 'Instalar')}
                </button>
            </div>
        </div>
    );
}

function PluginDetailModal({ plugin, onClose, onToggleInstall, installing }) {
    if (!plugin) return null;

    const badge = getPlanBadge(plugin.plan);
    const permissions = plugin.permissions || [];

    return (
        <>
            <div className={D.modalBackdrop} onClick={onClose} />
            <div className={D.modalPanel} onClick={onClose}>
                <div className={D.modalContainer} onClick={(e) => e.stopPropagation()}>
                    <div className={D.modalHeader}>
                        <div className={D.modalIcon}>
                            <Iconify icon={plugin.icon || 'eva:cube-outline'} width={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className={D.modalName}>{plugin.name}</h2>
                            <div className={D.modalMeta}>
                                <span className={`${D.badge} ${badge.className}`}>{badge.label}</span>
                                <span>{getCategoryLabel(plugin.category)}</span>
                                {plugin.comingSoon && (
                                    <span className={`${D.badge} ${D.badgeSoon}`}>Em breve</span>
                                )}
                            </div>
                            <p className={D.modalDeveloper}>
                                por {plugin.developer || 'LAIS'}
                            </p>
                        </div>
                        <button type="button" onClick={onClose} className={D.iconButton}>
                            <Iconify icon="eva:close-outline" width={16} />
                        </button>
                    </div>

                    <div className={D.modalBody}>
                        <div className={D.modalSection}>
                            <h3 className={D.modalSectionTitle}>Sobre</h3>
                            <p className={D.modalDescription}>
                                {plugin.longDescription || plugin.description}
                            </p>
                        </div>

                        {plugin.features && plugin.features.length > 0 && (
                            <div className={D.modalSection}>
                                <h3 className={D.modalSectionTitle}>Funcionalidades</h3>
                                <div className={D.modalSectionCard}>
                                    {plugin.features.map((feat, i) => (
                                        <div key={i} className={D.modalFeatureItem}>
                                            <span className={D.badgeDot} />
                                            {feat}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {permissions.length > 0 && (
                            <div className={D.modalSection}>
                                <h3 className={D.modalSectionTitle}>Permissões necessárias</h3>
                                <div className={D.modalSectionCard}>
                                    <div className={D.modalPermissionsGrid}>
                                        {permissions.map((perm, i) => (
                                            <div key={i} className={D.modalPermissionItem}>
                                                {perm}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={D.modalFooter}>
                        <span className={D.modalFooterPrice}>{plugin.price || 'Grátis'}</span>
                        {plugin.comingSoon ? (
                            <button type="button" className={D.primaryButton} disabled>
                                Em breve
                            </button>
                        ) : plugin.installed ? (
                            <button
                                type="button"
                                className={D.dangerButton}
                                onClick={() => onToggleInstall(plugin)}
                                disabled={installing}
                            >
                                {installing ? 'Desinstalando...' : 'Desinstalar'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={D.primaryButton}
                                onClick={() => onToggleInstall(plugin)}
                                disabled={installing}
                            >
                                {installing ? 'Instalando...' : 'Instalar'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default function PluginsComponent() {
    const { user, cliente, updateCliente } = useAuth();
    const [plugins, setPlugins] = useState([]);
    const [loading, setLoading] = useState(true);
    // Id do plugin em transito — um booleano unico colocaria todos os cards em loading.
    const [installingId, setInstallingId] = useState(null);
    const [search, setSearch] = useState('');
    const [selectedPlugin, setSelectedPlugin] = useState(null);

    const clienteId = user?.clienteId;

    useEffect(() => {
        if (clienteId) loadPlugins();
    }, [clienteId]);

    const loadPlugins = async () => {
        try {
            setLoading(true);
            const response = await pluginsService.listarPorCliente(clienteId);
            if (response?.data) setPlugins(response.data);
        } catch (error) {
            console.error('Erro ao carregar plugins:', error);
        } finally {
            setLoading(false);
        }
    };

    const syncClientePlugins = async (slug, installed) => {
        try {
            const response = await getActionClienteByClienteId(clienteId);
            const fresh = response?.data?.cliente || response?.data?.data || response?.data;
            if (fresh && (fresh._id || Array.isArray(fresh.plugins))) {
                updateCliente({
                    plugins: fresh.plugins || [],
                    features: fresh.features || cliente?.features || [],
                });
                return;
            }
        } catch (error) {
            console.error('Erro ao sincronizar cliente após plugin:', error);
        }

        const current = Array.isArray(cliente?.plugins) ? cliente.plugins : [];
        const next = installed
            ? Array.from(new Set([...current, slug]))
            : current.filter((p) => p !== slug);
        updateCliente({ plugins: next });
    };

    const handleToggleInstall = async (plugin) => {
        if (plugin.comingSoon) return;
        try {
            setInstallingId(plugin._id);
            const willInstall = !plugin.installed;
            if (plugin.installed) {
                await pluginsService.desinstalar(clienteId, plugin._id);
            } else {
                await pluginsService.instalar(clienteId, plugin._id);
            }

            await syncClientePlugins(plugin.slug, willInstall);

            const updated = (p) => (p._id === plugin._id ? { ...p, installed: willInstall } : p);
            setPlugins((prev) => prev.map(updated));
            setSelectedPlugin((prev) => (prev?._id === plugin._id ? { ...prev, installed: willInstall } : prev));
        } catch (error) {
            console.error('Erro ao alterar plugin:', error);
        } finally {
            setInstallingId(null);
        }
    };

    const filteredPlugins = useMemo(() => {
        if (!search.trim()) return plugins;
        const q = search.toLowerCase();
        return plugins.filter((p) => p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q));
    }, [plugins, search]);

    const groupedPlugins = useMemo(() => {
        const known = CATEGORIES.filter((c) => c.id !== 'all');
        const knownIds = new Set(known.map((c) => c.id));
        const groups = [];

        known.forEach((cat) => {
            const items = filteredPlugins.filter((p) => p.category === cat.id);
            if (items.length) groups.push({ id: cat.id, label: cat.label, items });
        });

        const extras = filteredPlugins.filter((p) => p.category && !knownIds.has(p.category));
        [...new Set(extras.map((p) => p.category))].forEach((id) => {
            groups.push({
                id,
                label: getCategoryLabel(id),
                items: extras.filter((p) => p.category === id),
            });
        });

        const unlabeled = filteredPlugins.filter((p) => !p.category);
        if (unlabeled.length) {
            const existingOutros = groups.find((g) => g.id === 'outros');
            if (existingOutros) existingOutros.items = [...existingOutros.items, ...unlabeled];
            else groups.push({ id: 'outros', label: 'Outros', items: unlabeled });
        }

        return groups;
    }, [filteredPlugins]);

    return (
        <div className={D.root} style={{ fontFamily: D.fontStack }}>
            <div className={D.stickyHeader}>
                <div className={D.container}>
                    <div className={D.topbar}>
                        <div className={D.topbarIdentity}>
                            <h1 className={D.title}>Plugins</h1>
                            <p className={D.subtitle}>Recursos extras instalados na conta.</p>
                        </div>
                        <div className={D.topbarActions}>
                            <div className={D.searchWrap}>
                                <Iconify icon="eva:search-outline" width={16} className={D.searchIcon} />
                                <Input
                                    placeholder="Buscar plugins"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className={D.searchInput}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`${D.container} pt-6 ${D.pageBody}`}>
                <div className={D.stack}>
                    {loading ? (
                        <div className={D.loadingWrap}>
                            <p className={D.loadingText}>Carregando plugins...</p>
                        </div>
                    ) : groupedPlugins.length > 0 ? (
                        groupedPlugins.map((group) => (
                            <div key={group.id} className={D.stack}>
                                <div className={D.sectionHeader}>
                                    <h2 className={D.sectionHeaderTitle}>{group.label}</h2>
                                    <p className={D.sectionHeaderSub}>
                                        {group.items.length === 1
                                            ? '1 plugin neste grupo.'
                                            : `${group.items.length} plugins neste grupo.`}
                                    </p>
                                </div>
                                <div className={D.grid}>
                                    {group.items.map((plugin) => (
                                        <PluginCard
                                            key={plugin._id || plugin.slug}
                                            plugin={plugin}
                                            onClick={setSelectedPlugin}
                                            onInstall={handleToggleInstall}
                                            installing={installingId === plugin._id}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={D.emptyWrap}>
                            <div className={D.emptyIcon}>
                                <Iconify icon="eva:cube-outline" width={28} />
                            </div>
                            <p className={D.emptyTitle}>Nenhum plugin encontrado</p>
                            <p className={D.emptyDesc}>Tente buscar com outro termo.</p>
                        </div>
                    )}
                </div>
            </div>

            <PluginDetailModal
                plugin={selectedPlugin}
                onClose={() => setSelectedPlugin(null)}
                onToggleInstall={handleToggleInstall}
                installing={installingId === selectedPlugin?._id}
            />
        </div>
    );
}
